import { Controller } from "@hotwired/stimulus"
import { PdfInlineViewer } from "../lib/pdf_inline_viewer"
import { watchOutsHtml, paintWatchOutsCard } from "../lib/contract_watch_outs"

// Received-contract page (Phelipe 2026-08-26): the PDF is the document —
// this controller only drives the ONE action the receiving choice allows
// (sign & send back, or send to another party) and the purple ask-about-it
// chat. Nothing here can change the document.
export default class extends Controller {
  static targets = [
    "statusPill",
    "signCard", "signedName", "returnEmail", "signError", "signBtn",
    "sendCard", "signerRows", "gcSigns", "sendSignedName", "sendError", "sendBtn",
    "chatMessages", "chatInput", "mainPoints", "watchOuts", "watchOutsCard",
    "pdfViewer", "zoomBar", "zoomLabel",
    "placeBtn", "placeStatus", "topActions",
    "history", "fieldList",
    "executedLink", "withdrawModal", "withdrawError", "withdrawBtn", "withdrawNotify",
    "copyModal", "copyList", "copyInput", "copyError", "copyBtn", "justSignBtn"
  ]
  static values = { askUrl: String, signUrl: String, sendUrl: String, summaryUrl: String,
                    pdfUrl: String, executedStatusUrl: String, pollExecuted: Boolean, withdrawUrl: String }

  connect() {
    if (this.hasMainPointsTarget && this.hasSummaryUrlValue) this._loadSummary()
    if (this.hasPdfViewerTarget && this.hasPdfUrlValue) {
      this._viewer = new PdfInlineViewer(this.pdfViewerTarget, this.pdfUrlValue)
      this._viewer.onZoom = z => this._paintZoom(z)
      this._wireViewer(this._viewer)
      this._viewer.load()
        .then(() => this._showZoomBar(true))
        .catch(() => this._iframeFallback())
    }
    // Signed before the executed render finished — keep watching so the
    // viewer swaps to the stamped copy the moment it exists.
    if (this.hasPollExecutedValue && this.pollExecutedValue) this._pollExecuted()
    this._keyHandler = e => this._onKey(e)
    document.addEventListener("keydown", this._keyHandler)
  }

  // ── Executed-copy swap (Phelipe 2026-08-27) ─────────────────────────────
  async _pollExecuted() {
    if (!this.hasExecutedStatusUrlValue) return
    this._executedTries = (this._executedTries || 0) + 1
    if (this._executedTries > 30) return
    try {
      const resp = await fetch(this.executedStatusUrlValue, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      const data = await resp.json().catch(() => ({}))
      if (data.url) return this._swapToExecuted(data.url)
    } catch (_) { /* poll again */ }
    this._executedTimer = setTimeout(() => this._pollExecuted(), 3000)
  }

  async _swapToExecuted(url) {
    // The stamped signature + certificate pages ARE in this copy — the
    // session marker comes down once the real thing is on screen.
    if (this._viewer) {
      try {
        const fresh = new PdfInlineViewer(this.pdfViewerTarget, url)
        fresh.onZoom = z => this._paintZoom(z)
        await fresh.load()
        this._viewer = fresh
        this._paintZoom(fresh.zoom)
      } catch (_) { /* keep showing the original */ }
    } else {
      this.pdfViewerTarget.innerHTML = `<iframe src="${this._esc(url)}" class="w-full h-full" style="border:0;"></iframe>`
    }
    if (this.hasTopActionsTarget && !this.topActionsTarget.querySelector("[data-executed-link]")) {
      this.topActionsTarget.insertAdjacentHTML("afterbegin",
        `<a href="${this._esc(url)}" target="_blank" rel="noopener" data-executed-link
            class="inline-flex items-center gap-1.5 px-3 py-1 text-[13px] font-medium text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg">Executed PDF</a>`)
    }
    if (this.hasSignCardTarget) {
      const hint = this.signCardTarget.querySelector("p")
      if (hint) hint.textContent = "The executed copy is ready — you're looking at it, audit page included."
    }
  }

  // pdf.js hiccup (worker missing, odd PDF) → plain embedded PDF, no
  // highlighting or zoom controls but everything else works.
  _iframeFallback() {
    this._viewer = null
    this._showZoomBar(false)
    this.pdfViewerTarget.innerHTML =
      `<iframe src="${this._esc(this.pdfUrlValue)}" class="w-full h-full" style="border:0;"></iframe>`
  }

  // Boxes dragged or resized on the paper (Kloepfer K3-7, 2026-09-24) come
  // back here, so Sign & send back sends where they really are now.
  _wireViewer(v) {
    v.onMarkerChange = m => {
      this._sigSpot = { page: m.page, x: m.x, y: m.y, size: m.size }
      if (this.hasPlaceStatusTarget) {
        this.placeStatusTarget.textContent = `Placed on page ${m.page} — drag it to move it, or drag its corner to resize.`
      }
    }
    v.onFieldChange = (id, p) => {
      const f = (this._fields || []).find(x => x.id === id)
      if (f) Object.assign(f, p)
    }
  }

  zoomInPdf() { this._viewer?.zoomIn() }
  zoomOutPdf() { this._viewer?.zoomOut() }

  // ── Signature placement (Phelipe 2026-08-27) ────────────────────────────
  togglePlacement() {
    if (!this._viewer) {
      if (this.hasPlaceStatusTarget) this.placeStatusTarget.textContent = "The PDF viewer couldn't open — the signature will ride the certificate page."
      return
    }
    if (this._placing) return this._stopPlacement()
    this._placing = true
    if (this.hasPlaceBtnTarget) {
      this.placeBtnTarget.textContent = "Now click the signature line on the paper…"
      this.placeBtnTarget.classList.add("border-brand", "text-brand", "bg-brand/5")
    }
    const label = (this.hasSignedNameTarget && this.signedNameTarget.value.trim()) || "Signature"
    this._viewer.enablePlacement(spot => {
      this._sigSpot = { ...spot, size: this._sigSpot?.size || 1 }
      this._viewer.setMarker(spot.page, spot.x, spot.y, label)
      if (this.hasPlaceStatusTarget) {
        this.placeStatusTarget.textContent = `Placed on page ${spot.page} — drag it to move it, or drag its corner to resize.`
      }
    })
  }

  // ── Date and text boxes (Larry K1-36, 2026-09-23) ──────────────────────
  // DocuSign-style: pick the kind, click the paper, and it lands there. Each
  // one is listed under the buttons so it can be edited or taken off again.
  placeDate(e) { this._placeField("date", e) }
  placeText(e) { this._placeField("text", e) }

  _placeField(kind, e) {
    if (e) e.preventDefault()
    if (!this._viewer) {
      if (this.hasPlaceStatusTarget) this.placeStatusTarget.textContent = "The PDF viewer couldn't open — date and text boxes need it."
      return
    }
    this._stopPlacement()
    this._viewer.setPlaceKind(kind)
    this._placing = true
    this._viewer.enablePlacement(spot => {
      if (spot.kind !== kind) return
      const id = `f${Date.now()}${Math.floor(Math.random() * 1000)}`
      const text = kind === "date" ? new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : ""
      this._fields = this._fields || []
      this._fields.push({ id, kind, page: spot.page, x: spot.x, y: spot.y, text, size: 1 })
      this._viewer.addField({ id, kind, page: spot.page, x: spot.x, y: spot.y, text, size: 1 })
      this._viewer.setPlaceKind("signature")
      this._stopPlacement()
      this._paintFieldList()
    })
  }

  _paintFieldList() {
    if (!this.hasFieldListTarget) return
    const fields = this._fields || []
    this.fieldListTarget.innerHTML = fields.map(f => `
      <div class="flex items-center gap-1.5" data-field-id="${f.id}">
        <span class="text-[11px] uppercase tracking-wide text-gray-400 w-8 shrink-0">${f.kind === "date" ? "Date" : "Text"}</span>
        <input type="text" value="${(f.text || "").replace(/"/g, "&quot;")}"
               placeholder="${f.kind === "date" ? "Date" : "What should it say?"}"
               data-action="input->received-contract#fieldTextChanged"
               class="flex-1 min-w-0 border border-gray-300 rounded-lg px-2 py-1 text-[12px]">
        <button type="button" data-action="click->received-contract#removeField"
                class="text-gray-300 hover:text-red-500 text-[15px] leading-none px-1 cursor-pointer" aria-label="Remove">&times;</button>
        <span class="text-[11px] text-gray-400 shrink-0">p.${f.page}</span>
      </div>`).join("")
  }

  fieldTextChanged(e) {
    const id = e.target.closest("[data-field-id]")?.dataset.fieldId
    if (!id) return
    const f = (this._fields || []).find(x => x.id === id)
    if (!f) return
    f.text = e.target.value
    this._viewer?.updateField(id, { text: f.text })
  }

  removeField(e) {
    if (e) e.preventDefault()
    const id = e.currentTarget.closest("[data-field-id]")?.dataset.fieldId
    if (!id) return
    this._fields = (this._fields || []).filter(x => x.id !== id)
    this._viewer?.removeField(id)
    this._paintFieldList()
  }

  _stopPlacement() {
    this._placing = false
    this._viewer?.disablePlacement()
    if (this.hasPlaceBtnTarget) {
      this.placeBtnTarget.textContent = this._sigSpot ? "Move the signature spot" : "Pick the spot on the document"
      this.placeBtnTarget.classList.remove("border-brand", "text-brand", "bg-brand/5")
    }
  }

  _showZoomBar(show) {
    if (!this.hasZoomBarTarget) return
    this.zoomBarTarget.classList.toggle("hidden", !show)
    this.zoomBarTarget.classList.toggle("flex", show)
  }

  _paintZoom(zoom) {
    if (this.hasZoomLabelTarget) this.zoomLabelTarget.textContent = `${Math.round(zoom * 100)}%`
  }

  // ── History (Larry K1-37, 2026-09-23) ───────────────────────────────────
  // Received contracts kept their events all along; this just opens them.
  openHistory(e) {
    if (e) e.preventDefault()
    if (!this.hasHistoryTarget) return
    this.historyTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  closeHistory(e) {
    if (e) e.preventDefault()
    if (!this.hasHistoryTarget) return
    this.historyTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  jumpWatchOut(e) {
    const btn = e.currentTarget
    const page = parseInt(btn.dataset.page, 10) || 1
    this._viewer?.highlight(page, btn.dataset.quote || "")
  }

  _onKey(e) {
    if (e.key === "Escape" && this.hasCopyModalTarget && !this.copyModalTarget.classList.contains("hidden")) {
      e.preventDefault()
      return this.hideCopy()
    }
    if (e.key === "Escape" && this.hasWithdrawModalTarget && !this.withdrawModalTarget.classList.contains("hidden")) {
      e.preventDefault()
      return this.hideWithdraw()
    }
    if (e.key === "Escape" && this.hasHistoryTarget && !this.historyTarget.classList.contains("hidden")) {
      e.preventDefault()
      this.closeHistory()
    }
  }

  disconnect() {
    if (this._keyHandler) document.removeEventListener("keydown", this._keyHandler)
    clearTimeout(this._summaryTimer)
    clearTimeout(this._executedTimer)
  }

  // ── Main points (right column) ──────────────────────────────────────────
  async _loadSummary(retry = false) {
    this._summaryTries = (this._summaryTries || 0) + 1
    if (this._summaryTries > 40) return this._summaryError()
    try {
      const url = this.summaryUrlValue + (retry ? "?retry=1" : "")
      const resp = await fetch(url, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      const data = await resp.json().catch(() => ({}))
      if (data.status === "done" && data.result) return this._renderSummary(data.result)
      if (data.status === "unavailable") { this.mainPointsTarget.closest("[class*=border-purple]")?.remove(); return }
      if (data.status === "failed" && !retry) return this._summaryError()
      // pending / processing — the read takes a little while on big PDFs.
      this._summaryTimer = setTimeout(() => this._loadSummary(), 4000)
    } catch (_) {
      this._summaryTimer = setTimeout(() => this._loadSummary(), 6000)
    }
  }

  retrySummary() {
    this._summaryTries = 0
    this.mainPointsTarget.innerHTML = `<div class="text-[13px] text-purple-600 animate-pulse">Reading the contract…</div>`
    this._loadSummary(true)
  }

  _summaryError() {
    this.mainPointsTarget.innerHTML =
      `<div class="text-[13px] text-gray-500">Couldn't read the contract.
         <button type="button" data-action="click->received-contract#retrySummary" class="text-purple-600 hover:underline cursor-pointer">Try again</button>
       </div>`
  }

  _renderSummary(r) {
    const esc = t => this._esc(t)
    const section = (label, html) => html
      ? `<div class="mb-3"><div class="text-[12px] font-semibold uppercase tracking-wide text-gray-400 mb-1">${esc(label)}</div>${html}</div>`
      : ""
    const line = t => t ? `<div class="text-[13px] text-gray-700 leading-relaxed">${esc(t)}</div>` : ""

    const parties = Array.isArray(r.parties) && r.parties.length
      ? r.parties.map(p => `<div class="text-[13px] text-gray-700"><span class="font-medium">${esc(p.name)}</span>${p.role ? ` <span class="text-gray-400">— ${esc(p.role)}</span>` : ""}</div>`).join("")
      : ""
    const dates = Array.isArray(r.key_dates) && r.key_dates.length
      ? r.key_dates.map(d => `<div class="flex items-baseline justify-between gap-2 text-[13px]"><span class="text-gray-500">${esc(d.label)}</span><span class="text-gray-800 text-right">${esc(d.value)}</span></div>`).join("")
      : ""
    // Watch-outs live in their own card under the chat (Phelipe 2026-08-28) —
    // they're the thing to act on, so they don't belong buried in the
    // reference detail on the other side of the page. Same rows as a sent
    // upload's Ask AI panel (lib/contract_watch_outs).
    const watch = watchOutsHtml(r.watch_outs, "received-contract", esc)
    if (this.hasWatchOutsTarget) {
      paintWatchOutsCard(this.hasWatchOutsCardTarget ? this.watchOutsCardTarget : null, this.watchOutsTarget, watch)
    }

    const html =
      section("Parties", parties) +
      section("Scope", line(r.scope)) +
      section("Contract value", line(r.contract_value)) +
      section("Key dates", dates) +
      section("Payment terms", line(r.payment_terms)) +
      section("Retainage", line(r.retainage)) +
      section("Termination", line(r.termination))

    // "Nothing notable" only when the watch-outs card came up empty too —
    // otherwise it contradicts the warnings sitting right next to it.
    this.mainPointsTarget.innerHTML = html || (watch
      ? `<div class="text-[13px] text-gray-500">See the watch-outs alongside the chat.</div>`
      : `<div class="text-[13px] text-gray-500">Nothing notable pulled from this document.</div>`)
  }

  // ── Sign & send back ────────────────────────────────────────────────────
  // The button asks first (Phelipe 2026-09-25): a pop-up names who gets the
  // signed copy — the address in the box — takes more, or signs without
  // emailing anyone ("Just sign").
  signAndReturn() {
    const name = this.signedNameTarget.value.trim()
    if (!name) return this._error(this.signErrorTarget, "Type your name to sign.")
    this._error(this.signErrorTarget, null)
    const box = this.hasReturnEmailTarget ? this.returnEmailTarget.value : ""
    this._copyTo = box.split(/[,;\s]+/).map(e => e.trim()).filter(e => this._emailOk(e))
      .filter((e, i, all) => all.findIndex(x => x.toLowerCase() === e.toLowerCase()) === i)
    if (!this.hasCopyModalTarget) return this._sign(this._copyTo)
    this._paintCopyList()
    this.copyInputTarget.value = ""
    this._error(this.copyErrorTarget, null)
    this._setCopyBusy(false)
    this.copyModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
    this.copyBtnTarget.focus()
  }

  hideCopy(e) {
    if (e) e.preventDefault()
    if (!this.hasCopyModalTarget || this._signing) return
    this.copyModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  copyInputKey(e) {
    if (e.key !== "Enter") return
    e.preventDefault()
    this.addCopyEmail()
  }

  // Returns false when the box holds something that isn't an email.
  addCopyEmail() {
    const email = this.copyInputTarget.value.trim()
    if (!email) return true
    if (!this._emailOk(email)) {
      this._error(this.copyErrorTarget, (this.copyModalTarget.dataset.invalidMsg || "%{email} doesn't look like an email address.").replace("%{email}", email))
      return false
    }
    if (!this._copyTo.some(e => e.toLowerCase() === email.toLowerCase())) this._copyTo.push(email)
    this.copyInputTarget.value = ""
    this._error(this.copyErrorTarget, null)
    this._paintCopyList()
    return true
  }

  removeCopyEmail(e) {
    const email = e.currentTarget.dataset.email
    this._copyTo = this._copyTo.filter(x => x !== email)
    this._paintCopyList()
  }

  signAndSend() {
    if (!this.addCopyEmail()) return // an email typed but not added yet goes along
    if (!this._copyTo.length) return this._error(this.copyErrorTarget, this.copyModalTarget.dataset.emptyMsg || "Add an email, or choose Just sign.")
    this._sign(this._copyTo)
  }

  justSign() { this._sign([]) }

  _emailOk(email) { return /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(email) }

  _paintCopyList() {
    const esc = t => this._esc(t)
    this.copyListTarget.innerHTML = this._copyTo.length
      ? this._copyTo.map(e => `<span class="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full bg-gray-100 text-[13px] text-gray-700">${esc(e)}
          <button type="button" data-action="click->received-contract#removeCopyEmail" data-email="${esc(e)}" aria-label="Remove ${esc(e)}"
                  class="w-5 h-5 inline-flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-200 cursor-pointer">&times;</button></span>`).join("")
      : `<span class="text-[13px] text-gray-500">${esc(this.copyModalTarget.dataset.emptyMsg || "Nobody yet.")}</span>`
  }

  _setCopyBusy(busy) {
    this._signing = busy
    if (this.hasCopyBtnTarget) this.copyBtnTarget.disabled = busy
    if (this.hasJustSignBtnTarget) this.justSignBtnTarget.disabled = busy
  }

  async _sign(emails) {
    const name = this.signedNameTarget.value.trim()
    const errorEl = this.hasCopyModalTarget ? this.copyErrorTarget : this.signErrorTarget
    this._error(errorEl, null)
    this._setCopyBusy(true)
    this.signBtnTarget.disabled = true
    this._stopPlacement()
    try {
      const spot = this._sigSpot
      const resp = await fetch(this.signUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({
          signed_name: name, signature_kind: "typed", return_emails: emails,
          // page,x,y — plus its size when he resized it on the paper (K3-7).
          signature_spot: spot ? `${spot.page},${spot.x.toFixed(1)},${spot.y.toFixed(1)}` +
            (spot.size && spot.size !== 1 ? `,${spot.size.toFixed(2)}` : "") : null,
          // The date and text boxes he dropped alongside it (Larry K1-36).
          signature_fields: (this._fields || []).map(f => ({
            kind: f.kind, page: f.page, x: Number(f.x.toFixed(1)), y: Number(f.y.toFixed(1)), text: f.text || "",
            size: f.size || 1
          }))
        })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't sign the contract — try again.")
      this._setCopyBusy(false)
      this.hideCopy()
      this.signCardTarget.innerHTML = this._doneCard(
        emails.length ? "Signed & sent back" : "Signed",
        emails.length ? `The executed copy goes to ${emails.join(", ")} as soon as it finishes rendering.`
                      : "Nobody on their side was emailed. The executed copy lands under Executed PDF above."
      ) + this._withdrawButton()
      this._paintPill("bg-green-50 text-green-700", "● On file")
      // The dashed preview becomes the signature right away, and the viewer
      // swaps to the stamped executed copy as soon as the job renders it.
      this._viewer?.markSigned(new Date().toLocaleDateString())
      this._executedTries = 0
      this._pollExecuted()
    } catch (err) {
      this._error(errorEl, err.message)
      this._setCopyBusy(false)
      this.signBtnTarget.disabled = false
    }
  }

  // ── Withdraw signature (Larry K2-1, 2026-09-24) ─────────────────────────
  // A received contract signed and sent back goes back to unsigned so it can
  // be signed again — on the line this time. Styled confirm first; the page
  // swaps to the sign form and the original PDF in place (no reload).
  askWithdraw(e) {
    if (e) e.preventDefault()
    if (!this.hasWithdrawModalTarget) return
    this._error(this.withdrawErrorTarget, null)
    if (this.hasWithdrawBtnTarget) this.withdrawBtnTarget.disabled = false
    // Emailing the other parties is asked each time (Phelipe 2026-09-24) — never pre-ticked.
    if (this.hasWithdrawNotifyTarget) this.withdrawNotifyTarget.checked = false
    this.withdrawModalTarget.classList.remove("hidden")
    document.body.style.overflow = "hidden"
  }

  hideWithdraw(e) {
    if (e) e.preventDefault()
    if (!this.hasWithdrawModalTarget) return
    this.withdrawModalTarget.classList.add("hidden")
    document.body.style.overflow = ""
  }

  stopPropagation(e) { e.stopPropagation() }

  async withdrawSignature() {
    if (!this.hasWithdrawUrlValue) return
    this.withdrawBtnTarget.disabled = true
    this._error(this.withdrawErrorTarget, null)
    try {
      const notify = this.hasWithdrawNotifyTarget && this.withdrawNotifyTarget.checked
      const resp = await fetch(this.withdrawUrlValue, { method: "POST", headers: this._headers(),
                                                        body: JSON.stringify({ notify_parties: notify }) })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !data.ok) throw new Error(data.error || "Couldn't withdraw the signature — try again.")
      // No more waiting on an executed copy for the signature that's gone.
      clearTimeout(this._executedTimer)
      this._executedTries = 999
      this._stopPlacement()
      this._sigSpot = null
      this._fields = []
      if (this.hasSignCardTarget && data.sign_form) this.signCardTarget.innerHTML = data.sign_form
      this._paintPill("bg-amber-50 text-amber-700", "● Awaiting my signature")
      if (this.hasTopActionsTarget) {
        this.topActionsTarget.querySelectorAll("[data-executed-link], [data-received-contract-target~='executedLink']").forEach(a => a.remove())
      }
      if (data.pdf_url) await this._swapViewer(data.pdf_url)
      this.hideWithdraw()
      this._showToast(data.notified > 0
        ? "Signature withdrawn and the other parties emailed — sign it again when you're ready"
        : "Signature withdrawn — sign it again when you're ready")
    } catch (err) {
      this._error(this.withdrawErrorTarget, err.message)
      this.withdrawBtnTarget.disabled = false
    }
  }

  // Back to the original paper, without the executed copy's stamp and certificate pages.
  async _swapViewer(url) {
    this.pdfUrlValue = url
    if (this._viewer) {
      try {
        const fresh = new PdfInlineViewer(this.pdfViewerTarget, url)
        fresh.onZoom = z => this._paintZoom(z)
        this._wireViewer(fresh)
        await fresh.load()
        this._viewer = fresh
        this._paintZoom(fresh.zoom)
        return
      } catch (_) { /* fall back to the plain embed below */ }
    }
    this._iframeFallback()
  }

  _withdrawButton() {
    if (!this.hasWithdrawModalTarget) return ""
    return `<button type="button" data-action="click->received-contract#askWithdraw"
              class="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-gray-600 hover:text-gray-900 cursor-pointer">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3"/></svg>
              Withdraw signature
            </button>`
  }

  // ── Needs another party ─────────────────────────────────────────────────
  addSignerRow() {
    const first = this.signerRowsTarget.querySelector("[data-signer-row]")
    const row = first.cloneNode(true)
    row.querySelectorAll("input").forEach(i => { i.value = "" })
    this.signerRowsTarget.appendChild(row)
  }

  async sendForSignature() {
    const signers = [...this.signerRowsTarget.querySelectorAll("[data-signer-row]")].map(row => ({
      name: row.querySelector("[data-signer-name]").value.trim(),
      email: row.querySelector("[data-signer-email]").value.trim(),
      role: "Signer"
    })).filter(s => s.name && s.email)
    if (!signers.length) return this._error(this.sendErrorTarget, "Add at least one signer with a name and email.")

    const gcSigns = this.hasGcSignsTarget && this.gcSignsTarget.checked
    const signedName = this.hasSendSignedNameTarget ? this.sendSignedNameTarget.value.trim() : ""
    if (gcSigns && !signedName) return this._error(this.sendErrorTarget, "Type your name to sign, or untick \"I sign it too\".")

    this._error(this.sendErrorTarget, null)
    this.sendBtnTarget.disabled = true
    try {
      const resp = await fetch(this.sendUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({
          signers: signers, gc_signs: gcSigns ? "true" : "false",
          signed_name: gcSigns ? signedName : null,
          signature_kind: gcSigns ? "typed" : null
        })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't send the contract — try again.")
      const rows = signers.map(s =>
        `<div class="flex items-center justify-between text-[13px]">
           <span class="text-gray-700 truncate">${this._esc(s.name)}</span>
           <span class="text-gray-400 shrink-0">Waiting</span>
         </div>`).join("")
      this.sendCardTarget.innerHTML = this._doneCard("Sent for signature",
        "Everyone below just got their signing link by email.") + `<div class="space-y-1.5 mt-2">${rows}</div>`
      this._paintPill("bg-amber-50 text-amber-700", "● Sent for signature")
    } catch (err) {
      this._error(this.sendErrorTarget, err.message)
      this.sendBtnTarget.disabled = false
    }
  }

  // ── Ask about this contract ─────────────────────────────────────────────
  suggest(e) { this._ask(e.currentTarget.textContent.trim()) }
  askEnter(e) { if (!e.shiftKey) { e.preventDefault(); this.ask() } }
  ask() {
    const q = this.chatInputTarget.value.trim()
    if (q) { this.chatInputTarget.value = ""; this._ask(q) }
  }

  async _ask(question) {
    if (this._asking) return
    this._asking = true
    const box = this.chatMessagesTarget
    box.insertAdjacentHTML("beforeend",
      `<div class="text-[13px] leading-relaxed bg-gray-100 rounded-lg px-2.5 py-1.5 ml-6">${this._esc(question)}</div>`)
    const pending = document.createElement("div")
    pending.className = "text-[13px] text-purple-500 px-1 animate-pulse"
    pending.textContent = "Reading the contract…"
    box.appendChild(pending)
    box.scrollTop = box.scrollHeight
    try {
      const resp = await fetch(this.askUrlValue, {
        method: "POST", headers: this._headers(), body: JSON.stringify({ question: question })
      })
      const data = await resp.json().catch(() => ({}))
      const answer = data.answer || data.reply || "I couldn't read that just now — try again."
      pending.className = "text-[13px] leading-relaxed text-gray-800 bg-purple-50 border border-purple-100 rounded-lg px-2.5 py-1.5 mr-6 whitespace-pre-wrap"
      pending.textContent = answer
    } catch (_) {
      pending.className = "text-[13px] text-red-600 px-1"
      pending.textContent = "I couldn't read that just now — try again."
    } finally {
      this._asking = false
      box.scrollTop = box.scrollHeight
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────────
  _doneCard(title, hint) {
    return `<div class="flex items-center gap-2 text-[13px] font-semibold text-gray-700 mb-1">
              <svg class="w-4 h-4 text-green-500" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>
              ${this._esc(title)}
            </div>
            <p class="text-[13px] text-gray-500">${this._esc(hint)}</p>`
  }

  // House toast (CLAUDE.md): prepended to the body, the flash controller fades it after 5 s.
  _showToast(message, type = "notice") {
    const cssType = type === "alert" || type === "error" ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(msg)
    document.body.prepend(el)
  }

  _paintPill(classes, label) {
    if (!this.hasStatusPillTarget) return
    this.statusPillTarget.className = `ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium shrink-0 ${classes}`
    this.statusPillTarget.textContent = label
  }

  _error(el, message) {
    if (!el) return
    el.classList.toggle("hidden", !message)
    el.textContent = message || ""
  }

  _headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  _esc(text) {
    const div = document.createElement("div")
    div.textContent = text ?? ""
    return div.innerHTML
  }
}
